import React, {
    useRef, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box, Button, Paper, Typography, CircularProgress,
} from '@material-ui/core';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import RefreshIcon from '@material-ui/icons/Refresh';

const useStyles = makeStyles(() => ({
    container: {
        padding: '1rem',
        backgroundColor: '#F7F9FF',
    },
    title: {
        fontFamily: 'Montserrat',
        fontWeight: 'bold',
        marginBottom: '1rem',
        textAlign: 'center',
        color: '#15325C',
    },
    hint: {
        fontFamily: 'Montserrat',
        fontSize: '0.75rem',
        color: '#666666',
        textAlign: 'center',
        marginBottom: '0.5rem',
    },
    videoContainer: {
        position: 'relative',
        backgroundColor: '#000000',
        borderRadius: '0.625rem',
        overflow: 'hidden',
        marginBottom: '1rem',
    },
    video: {
        width: '100%',
        height: 'auto',
        maxHeight: '300px',
        objectFit: 'cover',
        display: 'block',
    },
    capturedImage: {
        width: '100%',
        height: 'auto',
        maxHeight: '300px',
        objectFit: 'cover',
        borderRadius: '0.625rem',
        marginBottom: '1rem',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '200px',
        backgroundColor: '#F0F0F0',
        borderRadius: '0.625rem',
        marginBottom: '1rem',
    },
    loadingText: {
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#666666',
        marginTop: '0.5rem',
    },
    errorContainer: {
        backgroundColor: '#FFEBEE',
        border: '1px solid #EF5350',
        borderRadius: '0.625rem',
        padding: '1rem',
        marginBottom: '1rem',
    },
    errorText: {
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#C62828',
        textAlign: 'center',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '1rem',
    },
    captureButton: {
        background: 'linear-gradient(90deg, #E02A58, #C00333)',
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#FFFFFF',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        borderRadius: '0.625rem',
        boxShadow: '0.125rem 0.25rem 0.625rem #ED2D5D33',
        textTransform: 'capitalize',
        fontWeight: '600',
        '&:hover': {
            background: 'linear-gradient(90deg, #C00333, #A00228)',
        },
        '&:disabled': {
            background: '#CCCCCC',
            color: '#666666',
        },
    },
    retakeButton: {
        backgroundColor: '#F5F8FF',
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#C00333',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        borderRadius: '0.625rem',
        boxShadow: '0rem 0.125rem 0.25rem #ED2D5D33',
        textTransform: 'capitalize',
        fontWeight: '600',
        '&:hover': {
            backgroundColor: '#E8ECFF',
        },
    },
    hiddenCanvas: {
        display: 'none',
    },
}));

function Camera({ onCapture, onSkip }) {
    const classes = useStyles();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasStream, setHasStream] = useState(false);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(
                (track) => track.stop(),
            );
            streamRef.current = null;
            setHasStream(false);
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);

    const startCamera = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const mediaStream = await navigator
                .mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'user',
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                });
            streamRef.current = mediaStream;
            setHasStream(true);
            if (videoRef.current) {
                const video = videoRef.current;
                video.srcObject = mediaStream;

                const playVideo = async () => {
                    try {
                        await video.play();
                    } catch (playErr) {
                        // Ignorar errores de autoplay
                    }
                    setIsLoading(false);
                };

                if (video.readyState >= 1) {
                    playVideo();
                } else {
                    video.addEventListener(
                        'loadedmetadata',
                        playVideo,
                        { once: true },
                    );
                }
            }
        } catch (err) {
            setError(
                'No se pudo acceder a la camara.'
                + ' Verifica los permisos del navegador.',
            );
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        startCamera();
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(
                    (track) => track.stop(),
                );
                streamRef.current = null;
            }
        };
    }, []);

    const capturePhoto = useCallback(() => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            if (context) {
                // Redimensionar a max 240px de ancho
                const maxWidth = 240;
                const scale = Math.min(
                    maxWidth / video.videoWidth, 1,
                );
                canvas.width = video.videoWidth * scale;
                canvas.height = video.videoHeight * scale;
                context.drawImage(
                    video, 0, 0,
                    canvas.width, canvas.height,
                );

                const imageBase64 = canvas.toDataURL(
                    'image/jpeg', 0.4,
                );
                setCapturedImage(imageBase64);
                onCapture(imageBase64);
                stopCamera();
            }
        }
    }, [onCapture, stopCamera]);

    const retakePhoto = useCallback(() => {
        setCapturedImage(null);
        startCamera();
    }, [startCamera]);

    return (
      <Paper className={classes.container} elevation={3}>
        <Typography variant="h6" className={classes.title}>
          Captura de Identidad
        </Typography>
        <Typography className={classes.hint}>
          Tome una foto de su rostro para validar su identidad
        </Typography>

        {error && (
          <Box className={classes.errorContainer}>
            <Typography className={classes.errorText}>
              {error}
            </Typography>
          </Box>
        )}

        {!capturedImage && !error && (
          <Box className={classes.videoContainer}>
            {isLoading && (
              <Box
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    zIndex: 10,
                }}
              >
                <CircularProgress size={32} style={{ color: '#C00333' }} />
                <Typography className={classes.loadingText} style={{ color: '#FFFFFF' }}>
                  Iniciando camara...
                </Typography>
              </Box>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={classes.video}
            />
          </Box>
        )}

        {capturedImage && (
          <img
            src={capturedImage}
            alt="Foto capturada"
            className={classes.capturedImage}
          />
        )}

        <Box className={classes.buttonContainer}>
          {!capturedImage && !error && !isLoading && (
            <Button
              variant="contained"
              className={classes.captureButton}
              onClick={capturePhoto}
              disabled={!hasStream}
              startIcon={<CameraAltIcon />}
            >
              Capturar Foto
            </Button>
          )}

          {capturedImage && (
            <Button
              variant="contained"
              className={classes.retakeButton}
              onClick={retakePhoto}
              startIcon={<RefreshIcon />}
            >
              Repetir
            </Button>
          )}

          {onSkip && (
            <Button
              variant="text"
              onClick={onSkip}
              style={{
                  fontFamily: 'Montserrat',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#666666',
                  textTransform: 'capitalize',
              }}
            >
              Omitir
            </Button>
          )}
        </Box>

        <canvas ref={canvasRef} className={classes.hiddenCanvas} />
      </Paper>
    );
}

Camera.defaultProps = {
    onSkip: null,
};

Camera.propTypes = {
    onCapture: PropTypes.func.isRequired,
    onSkip: PropTypes.func,
};

export default Camera;
